<template>
   <section class="bgcc min-h-screen pt-20">
            <div class="min-h-screen from-cyan-50 via-blue-50 to-teal-50">
                <!-- Header Section -->
                <div class="form-header">
                    <div class="container mx-auto px-4">
                      <div class="max-w-4xl mx-auto text-center">
                          <!-- Breadcrumb Navigation -->
                          <nav class="flex items-center justify-center mb-4 text-sm">
                            <router-link
                              :to="{ name: 'FloodReliefMissionStatus' }"
                              class="flex items-center text-cyan-100 hover:text-white transition-colors"
                            >
                            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                              กลับหน้าตรวจสอบสถานะ
                            </router-link>
                            <svg class="w-4 h-4 mx-2 text-cyan-100" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                            <span class="text-white font-semibold">แก้ไขคำขอเบิกค่าเชื้อเพลิง</span>
                          </nav>
                          <h1 class="text-3xl font-bold mb-3">แก้ไขคำขอเบิกค่าเชื้อเพลิง</h1>
                          <p class="text-xl text-cyan-100">เลขที่คำขอ: {{ requestCode }}</p>
                      </div>
                    </div>
                </div>
        
                <!-- Navigation Menu (Sticky) -->
                <div class="sticky top-0 bg-white shadow-md z-40 border-b border-gray-200">
                    <div class="container mx-auto px-4">
                    <div class="max-w-4xl mx-auto">
                        <nav class="flex overflow-x-auto py-3 space-x-2">
                        <button @click="scrollToSection('organization')" 
                                class="flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
                                :class="activeSection === 'organization' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-teal-50'">
                            1. ข้อมูลกลุ่ม/องค์กร
                        </button>
                        <button @click="scrollToSection('coordinator')" 
                                class="flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
                                :class="activeSection === 'coordinator' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-teal-50'">
                            2. ผู้ประสานงาน
                        </button>
                        <button @click="scrollToSection('fieldwork')" 
                                class="flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
                                :class="activeSection === 'fieldwork' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-teal-50'">
                            3. ข้อมูลการลงพื้นที่
                        </button>
                        <button @click="scrollToSection('banking')" 
                                class="flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
                                :class="activeSection === 'banking' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-teal-50'">
                            4. ข้อมูลการโอนเงิน
                        </button>
                        <button @click="scrollToSection('others')" 
                                class="flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
                                :class="activeSection === 'others' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-teal-50'">
                            5. อื่นๆ
                        </button>
                        </nav>
                    </div>
                    </div>
                </div>
        
                <!-- Form Container -->
                <div class="container mx-auto px-4 py-8">
                    <div v-if="loading" class="text-center py-12">
                        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                        <p class="mt-2 text-gray-600">กำลังโหลดข้อมูล...</p>
                    </div>

                    <form v-else @submit.prevent="submitForm" class="space-y-8">
                        
                        <!-- Section 1: Organization Information -->
                        <div id="organization" ref="organization" class="form-section section-teal">
                        <div class="flex items-center mb-6">
                            <div class="section-number section-number-teal">
                            1
                            </div>
                            <h2 class="ml-4 text-2xl font-semibold text-gray-800">ข้อมูลกลุ่ม/องค์กร</h2>
                        </div>
        
                        <div class="space-y-6">
                            <!-- Organization Name -->
                            <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                ชื่อกลุ่ม/ชมรม/มูลนิธิ/องค์กร <span class="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                v-model="formData.organization_name"
                                :class="{'border-red-500': serverError.organization_name}"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                                placeholder="กรุณากรอกชื่อกลุ่ม/องค์กร">
                            <p v-if="serverError.organization_name" class="mt-1 text-sm text-red-500">
                                {{ serverError.organization_name[0] }}
                            </p>
                            </div>
        
                            <!-- Organization Address -->
                            <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                ที่อยู่ของกลุ่ม/ชมรม/มูลนิธิ/องค์กร <span class="text-red-500">*</span>
                            </label>
                            <textarea 
                                v-model="formData.organization_address"
                                :class="{'border-red-500': serverError.organization_address}"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                                rows="3"
                                placeholder="กรุณากรอกที่อยู่องค์กร"></textarea>
                            <p v-if="serverError.organization_address" class="mt-1 text-sm text-red-500">
                                {{ serverError.organization_address[0] }}
                            </p>
                            </div>
        
                            <!-- Organization Province -->
                            <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                จังหวัดขององค์กร <span class="text-red-500">*</span>
                            </label>
                            <select 
                                v-model="formData.organization_province"
                                :class="{'border-red-500': serverError.organization_province}"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors">
                                <option value="">-- เลือกจังหวัด --</option>
                                <option v-for="(province, index) in provinceOptions" :key="index" :value="province">
                                {{ province }}
                                </option>
                            </select>
                            <p v-if="serverError.organization_province" class="mt-1 text-sm text-red-500">
                                {{ serverError.organization_province[0] }}
                            </p>
                            </div>
        
                            <!-- Other Organization Province (Conditional) -->
                            <div v-if="formData.organization_province === 'อื่นๆ'">
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                ชื่อจังหวัดอื่นๆ <span class="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                v-model="formData.other_organization_province"
                                :class="{'border-red-500': serverError.other_organization_province}"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                                placeholder="กรุณาระบุชื่อจังหวัด">
                            <p v-if="serverError.other_organization_province" class="mt-1 text-sm text-red-500">
                                {{ serverError.other_organization_province[0] }}
                            </p>
                            </div>

                            <!-- Social Media Link -->
                            <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                              ลิงก์โซเชียลมีเดียของกลุ่ม/องค์กร <span class="text-gray-400 text-xs">(ไม่บังคับ)</span>
                            </label>
                            <input
                              type="url"
                              v-model="formData.social_media_link"
                              :class="{'border-red-500': serverError.social_media_link}"
                              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                              placeholder="https://facebook.com/your-group">
                            <p class="mt-1 text-xs text-gray-500">รองรับลิงก์ที่ขึ้นต้นด้วย http:// หรือ https://</p>
                            <p v-if="serverError.social_media_link" class="mt-1 text-sm text-red-500">
                              {{ serverError.social_media_link[0] }}
                            </p>
                            </div>

                            <!-- Additional Organization Details -->
                            <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                              รายละเอียดเพิ่มเติมเกี่ยวกับกลุ่ม/องค์กร <span class="text-gray-400 text-xs">(ไม่บังคับ)</span>
                            </label>
                            <textarea
                              v-model="formData.additional_details"
                              :class="{'border-red-500': serverError.additional_details}"
                              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                              rows="3"
                              placeholder="แนะนำภารกิจ ความเชี่ยวชาญ หรือช่องทางติดต่ออื่นๆ"></textarea>
                            <p class="mt-1 text-xs text-gray-500">สูงสุด 1,000 ตัวอักษร</p>
                            <p v-if="serverError.additional_details" class="mt-1 text-sm text-red-500">
                              {{ serverError.additional_details[0] }}
                            </p>
                            </div>
                        </div>
                        </div>
        
                        <!-- Section 2: Coordinator Information -->
                        <div id="coordinator" ref="coordinator" class="form-section section-cyan">
                        <div class="flex items-center mb-6">
                            <div class="section-number section-number-cyan">
                            2
                            </div>
                            <h2 class="ml-4 text-2xl font-semibold text-gray-800">ข้อมูลผู้ประสานงาน</h2>
                        </div>
        
                        <div class="space-y-6">
                            <!-- Coordinator Name -->
                            <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                ชื่อ-สกุลผู้ประสานงาน <span class="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                v-model="formData.coordinator_name"
                                :class="{'border-red-500': serverError.coordinator_name}"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors"
                                placeholder="กรุณากรอกชื่อ-สกุล">
                            <p v-if="serverError.coordinator_name" class="mt-1 text-sm text-red-500">
                                {{ serverError.coordinator_name[0] }}
                            </p>
                            </div>
        
                            <!-- Coordinator Phone -->
                            <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                เบอร์ติดต่อผู้ประสานงาน <span class="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                v-model="formData.coordinator_phone"
                                :class="{'border-red-500': serverError.coordinator_phone}"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors"
                                placeholder="กรุณากรอกเบอร์โทรศัพท์">
                            <p class="mt-1 text-xs text-gray-500">ตัวอย่าง: 0812345678</p>
                            <p v-if="serverError.coordinator_phone" class="mt-1 text-sm text-red-500">
                                {{ serverError.coordinator_phone[0] }}
                            </p>
                            </div>

                            <!-- Coordinator Email -->
                            <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                              อีเมลผู้ประสานงาน <span class="text-gray-400 text-xs">(ไม่บังคับ)</span>
                            </label>
                            <p class="mt-1 text-xs text-teal-600">แนะนำให้กรอกเพื่อรับการแจ้งเตือนสถานะการอนุมัติคำขอ</p>
                            <input 
                              type="email" 
                              v-model="formData.coordinator_email"
                              :class="{'border-red-500': serverError.coordinator_email}"
                              class="w-full mt-2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors"
                              placeholder="example@email.com">
                            <p v-if="serverError.coordinator_email" class="mt-1 text-sm text-red-500">
                              {{ serverError.coordinator_email[0] }}
                            </p>
                            </div>
                        </div>
                        </div>
        
                        <!-- Section 3: Field Work & Fuel Information -->
                        <div id="fieldwork" ref="fieldwork" class="form-section section-blue">
                        <div class="flex items-center mb-6">
                            <div class="section-number section-number-blue">
                            3
                            </div>
                            <h2 class="ml-4 text-2xl font-semibold text-gray-800">ข้อมูลการลงพื้นที่และเชื้อเพลิง</h2>
                        </div>
        
                        <div class="space-y-6">
                            <!-- Field Date -->
                            <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                วันที่ลงพื้นที่ <span class="text-red-500">*</span>
                            </label>
                            <input 
                                type="date" 
                                v-model="formData.field_date"
                                :class="{'border-red-500': serverError.field_date}"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors">
                            <p v-if="serverError.field_date" class="mt-1 text-sm text-red-500">
                                {{ serverError.field_date[0] }}
                            </p>
                            </div>
        
                            <!-- Fuel For -->
                            <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                น้ำมันเชื้อเพลิงนำไปใช้สำหรับ <span class="text-red-500">*</span>
                            </label>
                            <select 
                                v-model="formData.fuel_for"
                                :class="{'border-red-500': serverError.fuel_for}"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors">
                                <option value="">-- เลือกประเภทพาหนะ --</option>
                                <option v-for="(option, index) in fuelForOptions" :key="index" :value="option">
                                {{ option }}
                                </option>
                            </select>
                            <p v-if="serverError.fuel_for" class="mt-1 text-sm text-red-500">
                                {{ serverError.fuel_for[0] }}
                            </p>
                            </div>
        
                            <!-- Other Fuel For (Conditional) -->
                            <div v-if="formData.fuel_for === 'อื่นๆ'">
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                ระบุประเภทอื่นๆ <span class="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                v-model="formData.other_fuel_for"
                                :class="{'border-red-500': serverError.other_fuel_for}"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                placeholder="กรุณาระบุประเภทพาหนะ">
                            <p v-if="serverError.other_fuel_for" class="mt-1 text-sm text-red-500">
                                {{ serverError.other_fuel_for[0] }}
                            </p>
                            </div>
        
                            <!-- Vehicle Count -->
                            <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                จำนวนพาหนะ (คัน) <span class="text-red-500">*</span>
                            </label>
                            <input 
                                type="number" 
                                v-model="formData.vehicle_count"
                                :class="{'border-red-500': serverError.vehicle_count}"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                placeholder="กรุณากรอกจำนวนพาหนะ"
                                min="1">
                            <p v-if="serverError.vehicle_count" class="mt-1 text-sm text-red-500">
                                {{ serverError.vehicle_count[0] }}
                            </p>
                            </div>
        
                            <!-- Total Amount -->
                            <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                จำนวนเงินทั้งสิ้น (บาท) <span class="text-red-500">*</span>
                            </label>
                            <input 
                                type="number" 
                                v-model="formData.total_amount"
                                :class="{'border-red-500': serverError.total_amount}"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                placeholder="กรุณากรอกจำนวนเงิน"
                                step="0.01"
                                min="0">
                            <p class="mt-1 text-xs text-gray-500">หมายเหตุ: ครั้งละไม่เกิน 1,000 บาทต่อใบเสร็จ</p>
                            <p v-if="serverError.total_amount" class="mt-1 text-sm text-red-500">
                                {{ serverError.total_amount[0] }}
                            </p>
                            </div>
        
                            <!-- Community Names -->
                            <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                ชื่อชุมชนที่ลงพื้นที่ <span class="text-red-500">*</span>
                            </label>
                            <textarea 
                                v-model="formData.community_names"
                                :class="{'border-red-500': serverError.community_names}"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                rows="3"
                                placeholder="กรุณากรอกชื่อชุมชน"></textarea>
                            <p class="mt-1 text-xs text-gray-500">หมายเหตุ: สามารถระบุได้มากกว่า 1 ชุมชน</p>
                            <p v-if="serverError.community_names" class="mt-1 text-sm text-red-500">
                                {{ serverError.community_names[0] }}
                            </p>
                            </div>
        
                            <!-- Field Provinces (Checkbox) -->
                            <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                จังหวัดที่ลงพื้นที่ <span class="text-red-500">*</span>
                            </label>
                            <p class="text-xs text-gray-500 mb-3">หมายเหตุ: สามารถระบุได้มากกว่า 1 จังหวัด</p>
                            <div class="space-y-2">
                                <div v-for="(province, index) in provinceOptions" :key="index" class="flex items-center">
                                <input 
                                    type="checkbox" 
                                    :id="'field_province_' + index"
                                    :value="province"
                                    v-model="formData.field_provinces"
                                    class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500">
                                <label :for="'field_province_' + index" class="ml-2 text-sm text-gray-700">
                                    {{ province }}
                                </label>
                                </div>
                            </div>
                            <p v-if="serverError.field_provinces" class="mt-1 text-sm text-red-500">
                                {{ serverError.field_provinces[0] }}
                            </p>
                            </div>
        
                            <!-- Other Field Provinces (Conditional) -->
                            <div v-if="formData.field_provinces.includes('อื่นๆ')">
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                จังหวัดอื่นๆ ที่ลงพื้นที่ <span class="text-red-500">*</span>
                            </label>
                            <textarea 
                                v-model="formData.other_field_provinces"
                                :class="{'border-red-500': serverError.other_field_provinces}"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                rows="2"
                                placeholder="กรุณาระบุชื่อจังหวัดอื่นๆ"></textarea>
                            <p class="mt-1 text-xs text-gray-500">ตัวอย่าง: กรุงเทพมหานคร, นนทบุรี</p>
                            <p v-if="serverError.other_field_provinces" class="mt-1 text-sm text-red-500">
                                {{ serverError.other_field_provinces[0] }}
                            </p>
                            </div>
        
                            <!-- Additional Notes -->
                            <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                อธิบายเพิ่มเติม สถานการณ์ / รถที่ไป และอื่นๆ
                            </label>
                            <textarea 
                                v-model="formData.additional_notes"
                                :class="{'border-red-500': serverError.additional_notes}"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                rows="5"
                                placeholder="กรุณากรอกรายละเอียดเพิ่มเติม"
                                maxlength="1000"></textarea>
                            <p class="mt-1 text-xs text-gray-500">ไม่เกิน 1,000 คำ (ใช้ไปแล้ว {{ formData.additional_notes ? formData.additional_notes.length : 0 }}/1000 ตัวอักษร)</p>
                            <p v-if="serverError.additional_notes" class="mt-1 text-sm text-red-500">
                                {{ serverError.additional_notes[0] }}
                            </p>
                            </div>

                            <!-- Fieldwork Social Media Link -->
                            <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                ลิงก์โซเชียลมีเดียการลงพื้นที่ขององค์กรท่าน 
                                <!-- <span class="text-gray-400 text-xs">(ไม่บังคับ)</span> -->
                            </label>
                            <input 
                                type="url" 
                                v-model="formData.fieldwork_social_media_link"
                                :class="{'border-red-500': serverError.fieldwork_social_media_link}"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                placeholder="https://facebook.com/post/xxx">
                            <p class="mt-1 text-xs text-gray-500">ลิงก์โพสต์ Facebook, รูปภาพ หรือวิดีโอที่แสดงการลงพื้นที่ช่วยเหลือ</p>
                            <p v-if="serverError.fieldwork_social_media_link" class="mt-1 text-sm text-red-500">
                                {{ serverError.fieldwork_social_media_link[0] }}
                            </p>
                            </div>
                        </div>
                        </div>
        
                        <!-- Section 4: Banking & Documents -->
                        <div id="banking" ref="banking" class="bg-white rounded-lg shadow-md p-6 border-t-4 border-indigo-600 scroll-mt-24">
                        <div class="flex items-center mb-6">
                            <div class="flex-shrink-0 w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                            4
                            </div>
                            <h2 class="ml-4 text-2xl font-semibold text-gray-800">ข้อมูลการโอนเงินและเอกสาร</h2>
                        </div>
        
                        <div class="space-y-6">
                            <!-- Bank Name -->
                            <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                ชื่อธนาคารของกลุ่มหรือผู้ประสานงาน <span class="text-red-500">*</span>
                            </label>
                            <select 
                                v-model="formData.bank_name"
                                :class="{'border-red-500': serverError.bank_name}"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors">
                                <option value="">-- เลือกธนาคาร --</option>
                                <option v-for="(bank, index) in bankOptions" :key="index" :value="bank">
                                {{ bank }}
                                </option>
                            </select>
                            <p v-if="serverError.bank_name" class="mt-1 text-sm text-red-500">
                                {{ serverError.bank_name[0] }}
                            </p>
                            </div>
        
                            <!-- Other Bank Name (Conditional) -->
                            <div v-if="formData.bank_name === 'อื่นๆ'">
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                ชื่อธนาคารอื่นๆ <span class="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                v-model="formData.other_bank_name"
                                :class="{'border-red-500': serverError.other_bank_name}"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                placeholder="กรุณาระบุชื่อธนาคาร">
                            <p v-if="serverError.other_bank_name" class="mt-1 text-sm text-red-500">
                                {{ serverError.other_bank_name[0] }}
                            </p>
                            </div>
        
                            <!-- Account Name -->
                            <div>
                              <label class="block text-sm font-medium text-gray-700 mb-2">
                                  ชื่อบัญชีธนาคาร <span class="text-red-500">*</span>
                              </label>
                              <input 
                                  type="text" 
                                  v-model="formData.account_name"
                                  :class="{'border-red-500': serverError.account_name}"
                                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                  placeholder="กรุณากรอกชื่อบัญชี">
                              <p v-if="serverError.account_name" class="mt-1 text-sm text-red-500">
                                  {{ serverError.account_name[0] }}
                              </p>
                            </div>
          
                            <!-- Account Number -->
                            <div>
                              <label class="block text-sm font-medium text-gray-700 mb-2">
                                  เลขบัญชีธนาคาร <span class="text-red-500">*</span>
                              </label>
                              <input 
                                  type="text" 
                                  v-model="formData.account_number"
                                  :class="{'border-red-500': serverError.account_number}"
                                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                  placeholder="กรุณากรอกเลขบัญชี">
                              <p class="mt-1 text-xs text-gray-500">ตัวอย่าง: 1234567890</p>
                              <p v-if="serverError.account_number" class="mt-1 text-sm text-red-500">
                                  {{ serverError.account_number[0] }}
                              </p>
                            </div>

                            <!-- Book Bank Photos (Optional) -->
                            <div>
                              <label class="block text-sm font-medium text-gray-700 mb-2">
                                รูปถ่ายหน้าบัญชี <span class="text-red-500">*</span>
                              </label>
                              
                              <!-- Existing Files -->
                              <div v-if="existingBookBankPhotos.length > 0" class="mb-4">
                                <p class="text-sm font-medium text-gray-600 mb-2">รูปเดิม:</p>
                                <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                                  <div v-for="file in existingBookBankPhotos" :key="file.id" class="relative group">
                                    <img :src="getFileUrl(file)" class="w-full h-32 object-cover rounded-lg border-2 border-gray-200">
                                    <button 
                                      type="button"
                                      @click="removeExistingFile(file.id, 'book_bank')"
                                      class="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              </div>

                              <input 
                                type="file" 
                                @change="onBookBankPhotoChange"
                                :class="{'border-red-500': serverError.book_bank_photos}"
                                class="w-full mt-2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                accept="image/jpeg,image/jpg,image/png">
                              <p class="mt-1 text-xs text-gray-500">
                                อัพโหลดได้ 1 รูป • ขนาดไฟล์ไม่เกินรูปละ 10 MB • รองรับเฉพาะไฟล์ JPG, JPEG, PNG
                              </p>
                              <p v-if="serverError.book_bank_photos" class="mt-1 text-sm text-red-500">
                                {{ serverError.book_bank_photos[0] }}
                              </p>

                              <div v-if="bookBankPhotoPreviews.length > 0" class="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                                <div v-for="(preview, index) in bookBankPhotoPreviews" :key="index" class="relative group">
                                <img :src="preview.url" :alt="'รูปหน้าบัญชี ' + (index + 1)" class="w-full h-32 object-cover rounded-lg border-2 border-gray-200">
                                <button 
                                  type="button"
                                  @click="removeBookBankPhoto(index)"
                                  class="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                  </svg>
                                </button>
                                <p class="text-xs text-gray-500 mt-1 text-center truncate">{{ preview.name }}</p>
                                </div>
                              </div>
                            </div>
          
                            <!-- Payment Slips -->
                            <div>
                              <label class="block text-sm font-medium text-gray-700 mb-2">
                                  รูปสลิปการจ่ายเงิน <span class="text-red-500">*</span>
                              </label>

                              <!-- Existing Files -->
                              <div v-if="existingPaymentSlips.length > 0" class="mb-4">
                                <p class="text-sm font-medium text-gray-600 mb-2">รูปเดิม:</p>
                                <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                                  <div v-for="file in existingPaymentSlips" :key="file.id" class="relative group">
                                    <img :src="getFileUrl(file)" class="w-full h-32 object-cover rounded-lg border-2 border-gray-200">
                                    <button 
                                      type="button"
                                      @click="removeExistingFile(file.id, 'payment_slip')"
                                      class="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              </div>

                              <input 
                                  type="file" 
                                  @change="onPaymentSlipChange"
                                  :class="{'border-red-500': serverError.payment_slips}"
                                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                  accept="image/jpeg,image/jpg,image/png"
                                  multiple>
                              <p class="mt-1 text-xs text-gray-500">
                                  อัพโหลดได้ไม่เกิน 5 รูป • ขนาดไฟล์ไม่เกินรูปละ 10 MB • รองรับเฉพาะไฟล์ JPG, JPEG, PNG
                              </p>
                              <p v-if="serverError.payment_slips" class="mt-1 text-sm text-red-500">
                                  {{ serverError.payment_slips[0] }}
                              </p>
          
                              <!-- Payment Slips Preview -->
                              <div v-if="paymentSlipPreviews.length > 0" class="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                                  <div v-for="(preview, index) in paymentSlipPreviews" :key="index" class="relative group">
                                  <img :src="preview.url" :alt="'สลิป ' + (index + 1)" class="w-full h-32 object-cover rounded-lg border-2 border-gray-200">
                                  <button 
                                      type="button"
                                      @click="removePaymentSlip(index)"
                                      class="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                      </svg>
                                  </button>
                                  <p class="text-xs text-gray-500 mt-1 text-center truncate">{{ preview.name }}</p>
                                  </div>
                              </div>
                            </div>
      
                            <!-- Refueling Photos (Optional) -->
                            <div>
                              <label class="block text-sm font-medium text-gray-700 mb-2">
                                  รูปในขณะเติมน้ำมันพาหนะ (ถ้ามี)
                              </label>

                              <!-- Existing Files -->
                              <div v-if="existingRefuelingPhotos.length > 0" class="mb-4">
                                <p class="text-sm font-medium text-gray-600 mb-2">รูปเดิม:</p>
                                <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                                  <div v-for="file in existingRefuelingPhotos" :key="file.id" class="relative group">
                                    <img :src="getFileUrl(file)" class="w-full h-32 object-cover rounded-lg border-2 border-gray-200">
                                    <button 
                                      type="button"
                                      @click="removeExistingFile(file.id, 'refueling')"
                                      class="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              </div>

                              <input 
                                  type="file" 
                                  @change="onRefuelingPhotoChange"
                                  :class="{'border-red-500': serverError.refueling_photos}"
                                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                  accept="image/jpeg,image/jpg,image/png"
                                  multiple>
                              <p class="mt-1 text-xs text-gray-500">
                                  อัพโหลดได้ไม่เกิน 5 รูป • ขนาดไฟล์ไม่เกินรูปละ 10 MB • รองรับเฉพาะไฟล์ JPG, JPEG, PNG
                              </p>
                              <p v-if="serverError.refueling_photos" class="mt-1 text-sm text-red-500">
                                  {{ serverError.refueling_photos[0] }}
                              </p>
          
                              <!-- Refueling Photos Preview -->
                              <div v-if="refuelingPhotoPreviews.length > 0" class="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                                  <div v-for="(preview, index) in refuelingPhotoPreviews" :key="index" class="relative group">
                                  <img :src="preview.url" :alt="'รูปเติมน้ำมัน ' + (index + 1)" class="w-full h-32 object-cover rounded-lg border-2 border-gray-200">
                                  <button 
                                      type="button"
                                      @click="removeRefuelingPhoto(index)"
                                      class="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                      </svg>
                                  </button>
                                  <p class="text-xs text-gray-500 mt-1 text-center truncate">{{ preview.name }}</p>
                                  </div>
                              </div>
                            </div>
                        </div>
                        </div>
        
                        <!-- Section 5: Others -->
                        <div id="others" ref="others" class="form-section section-purple">
                        <div class="flex items-center mb-6">
                            <div class="section-number section-number-purple">
                            5
                            </div>
                            <h2 class="ml-4 text-2xl font-semibold text-gray-800">อื่นๆ</h2>
                        </div>
        
                        <div class="space-y-6">
                            <!-- Remarks -->
                            <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                หมายเหตุ / ข้อเสนอแนะ / อื่นๆ <span class="text-gray-400 text-xs">(ไม่บังคับ)</span>
                            </label>
                            <textarea 
                                v-model="formData.remarks"
                                :class="{'border-red-500': serverError.remarks}"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                                rows="4"
                                placeholder="กรุณากรอกหมายเหตุหรือข้อเสนอแนะ (ถ้ามี)"
                                maxlength="1000"></textarea>
                            <p class="mt-1 text-xs text-gray-500">ไม่เกิน 1,000 ตัวอักษร (ใช้ไปแล้ว {{ formData.remarks ? formData.remarks.length : 0 }}/1000 ตัวอักษร)</p>
                            <p v-if="serverError.remarks" class="mt-1 text-sm text-red-500">
                                {{ serverError.remarks[0] }}
                            </p>
                            </div>
                        </div>
                        </div>

                        <!-- Submit Button -->
                        <div class="bg-white rounded-lg shadow-md p-6">
                        <div class="flex flex-col sm:flex-row gap-4 justify-center items-center">
                            <button 
                            type="button"
                            @click="$router.push({ name: 'FloodReliefMissionStatus' })"
                            class="w-full sm:w-auto px-8 py-3 bg-gray-500 text-white rounded-lg font-medium hover:bg-gray-600 transition-colors"
                            :disabled="isSubmitting">
                            ยกเลิก
                            </button>
                            <button 
                            type="submit"
                            :disabled="isSubmitting || isCompressing"
                            :class="{'opacity-50 cursor-not-allowed': isSubmitting || isCompressing}"
                            class="w-full sm:w-auto px-8 py-3 section-number-cyan text-white rounded-lg font-medium hover:from-teal-700 hover:to-cyan-700 transition-all shadow-lg hover:shadow-xl">
                            <span v-if="!isSubmitting">บันทึกการแก้ไข</span>
                            <span v-else>กำลังบันทึก...</span>
                            </button>
                        </div>
                        </div>
        
                    </form>
                </div>
            </div>

            <!-- Compression Progress Overlay -->
            <div v-if="isCompressing" class="compression-overlay">
              <div class="compression-modal">
                <div class="compression-spinner"></div>
                <h3 class="text-lg font-semibold text-gray-800 mt-4">กำลังบีบอัดรูปภาพ...</h3>
                <p class="text-sm text-gray-600 mt-1 truncate max-w-xs">{{ currentCompressingFile }}</p>
                <div class="compression-progress-bar mt-4">
                  <div class="compression-progress-fill" :style="{ width: compressionProgress + '%' }"></div>
                </div>
                <p class="text-sm font-medium text-teal-600 mt-2">{{ compressionProgress }}%</p>
                <p class="text-xs text-gray-500 mt-2">กรุณารอสักครู่ ระบบกำลังลดขนาดไฟล์เพื่อให้อัพโหลดได้เร็วขึ้น</p>
              </div>
            </div>
    </section>
</template>

<script>
import FloodReliefMissionService from '@/services/FloodReliefMissionService'
import imageCompression from 'browser-image-compression'

function deriveAssetBaseUrl() {
    const apiUrl = process.env.VUE_APP_URL_API || ''
    if (!apiUrl) {
        return window.location.origin + '/'
    }
    return apiUrl.replace(/\/api\/?$/, '/')
}

export default {
  name: 'FloodReliefMissionEdit',
  components: {
  },
  data() {
    return {
      loading: true,
      activeSection: 'organization',
      isSubmitting: false,
      isCompressing: false,
      compressionProgress: 0,
      currentCompressingFile: '',
      requestCode: '',
      assetBaseUrl: deriveAssetBaseUrl(),
      
      // Form data
      formData: {
        // Organization
        organization_name: '',
        organization_address: '',
        organization_province: '',
        other_organization_province: '',
        social_media_link: '',
        additional_details: '',
        
        // Coordinator
        coordinator_name: '',
        coordinator_phone: '',
        coordinator_email: '',
        
        // Field work & Fuel
        field_date: '',
        fuel_for: '',
        other_fuel_for: '',
        vehicle_count: '',
        total_amount: '',
        community_names: '',
        field_provinces: [],
        other_field_provinces: '',
        additional_notes: '',
        fieldwork_social_media_link: '',
        
        // Banking
        bank_name: '',
        other_bank_name: '',
        account_name: '',
        account_number: '',

        // Others
        remarks: '',
      },
      
      // Existing files
      existingPaymentSlips: [],
      existingBookBankPhotos: [],
      existingRefuelingPhotos: [],
      filesToRemove: [],

      // New File uploads
      paymentSlips: [],
      paymentSlipPreviews: [],
      bookBankPhotos: [],
      bookBankPhotoPreviews: [],
      refuelingPhotos: [],
      refuelingPhotoPreviews: [],
      
      // Dropdown options
      provinceOptions: [
        'ปัตตานี',
        'ยะลา',
        'นราธิวาส',
        'สงขลา',
        'อื่นๆ'
      ],
      
      fuelForOptions: [
        'รถยนต์',
        'เรือ',
        'มอเตอร์ไซค์',
        'อื่นๆ'
      ],
      
      bankOptions: [
        'ธนาคารกรุงเทพ',
        'ธนาคารกสิกรไทย',
        'ธนาคารกรุงไทย',
        'ธนาคารทหารไทยธนชาต',
        'ธนาคารไทยพาณิชย์',
        'ธนาคารกรุงศรีอยุธยา',
        'ธนาคารเกียรตินาคินภัทร',
        'ธนาคารซีไอเอ็มบีไทย',
        'ธนาคารทิสโก้',
        'ธนาคารธนชาต',
        'ธนาคารยูโอบี',
        'ธนาคารสแตนดาร์ดชาร์เตอร์ด (ไทย)',
        'ธนาคารออมสิน',
        'ธนาคารอาคารสงเคราะห์',
        'ธนาคารเพื่อการเกษตรและสหกรณ์การเกษตร (ธ.ก.ส.)',
        'ธนาคารเพื่อการส่งออกและนำเข้าแห่งประเทศไทย',
        'ธนาคารอิสลามแห่งประเทศไทย',
        'ธนาคารแลนด์ แอนด์ เฮ้าส์',
        'ธนาคารไอซีบีซี (ไทย)',
        'ธนาคารพัฒนาวิสาหกิจขนาดกลางและขนาดย่อมแห่งประเทศไทย',
        'ธนาคารลิเบอเรชั่น',
        'ธนาคารซูมิโตโม มิตซุย ทรัสต์ (ไทย)',
        'อื่นๆ'
      ],
      
      // Error handling
      serverError: {}
    }
  },
  computed: {
    encryptedId() {
      return this.$route.params.encryptedId
    }
  },
  mounted() {
    // Scroll to top on mount
    window.scrollTo(0, 0)
    // Setup scroll listener for active section
    window.addEventListener('scroll', this.handleScroll)
    
    // Fetch data
    this.fetchRequestData()
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    async fetchRequestData() {
      this.loading = true
      try {
        const response = await FloodReliefMissionService.getRequestForEdit(this.encryptedId)
        const data = response.data.data
        
        this.requestCode = data.request_code
        
        // Populate form data
        this.formData = {
          organization_name: data.organization_name,
          organization_address: data.organization_address,
          organization_province: data.organization_province,
          other_organization_province: data.other_organization_province,
          social_media_link: data.social_media_link,
          additional_details: data.additional_details,
          
          coordinator_name: data.coordinator_name,
          coordinator_phone: data.coordinator_phone,
          coordinator_email: data.coordinator_email,
          
          field_date: data.field_date,
          fuel_for: data.fuel_for,
          other_fuel_for: data.other_fuel_for,
          vehicle_count: data.vehicle_count,
          total_amount: data.total_amount,
          community_names: data.community_names,
          field_provinces: typeof data.field_provinces === 'string' ? JSON.parse(data.field_provinces) : data.field_provinces,
          other_field_provinces: data.other_field_provinces,
          additional_notes: data.additional_notes,
          fieldwork_social_media_link: data.fieldwork_social_media_link,
          
          bank_name: data.bank_name,
          other_bank_name: data.other_bank_name,
          account_name: data.account_name,
          account_number: data.account_number,
          
          remarks: data.remarks,
        }

        // Populate existing files
        if (data.payment_slips) {
          this.existingPaymentSlips = data.payment_slips
        }
        if (data.book_bank_photos) {
          this.existingBookBankPhotos = data.book_bank_photos
        }
        if (data.refueling_photos) {
          this.existingRefuelingPhotos = data.refueling_photos
        }

      } catch (error) {
        this.$swal.fire({
          icon: 'error',
          title: 'ไม่สามารถโหลดข้อมูลได้',
          text: (error.response && error.response.data && error.response.data.message) || 'เกิดข้อผิดพลาดในการโหลดข้อมูล',
          confirmButtonText: 'กลับ',
          confirmButtonColor: '#0d9488'
        }).then(() => {
          this.$router.push({ name: 'FloodReliefMissionStatus' })
        })
      } finally {
        this.loading = false
      }
    },

    getFileUrl(file) {
      if (!file) return ''
      if (file.path) {
        return this.normalizeAssetUrl(file.path)
      }
      return ''
    },

    normalizeAssetUrl(path) {
      if (!path) return ''
      if (/^https?:\/\//i.test(path)) return path
      
      const base = this.assetBaseUrl.replace(/\/+$/, '')
      const cleanPath = String(path).replace(/^\/+/, '')
      return `${base}/${cleanPath}`
    },

    removeExistingFile(fileId, type) {
      this.filesToRemove.push(fileId)
      
      if (type === 'payment_slip') {
        this.existingPaymentSlips = this.existingPaymentSlips.filter(f => f.id !== fileId)
      } else if (type === 'book_bank') {
        this.existingBookBankPhotos = this.existingBookBankPhotos.filter(f => f.id !== fileId)
      } else if (type === 'refueling') {
        this.existingRefuelingPhotos = this.existingRefuelingPhotos.filter(f => f.id !== fileId)
      }
    },

    // Image compression helper
    async compressImage(file, maxSizeMB = 1.5) {
      // Skip compression if file is already small enough
      if (file.size <= maxSizeMB * 1024 * 1024) {
        return file
      }

      const options = {
        maxSizeMB,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        initialQuality: 0.8,
        fileType: 'image/jpeg',
        onProgress: (progress) => {
          this.compressionProgress = progress
        }
      }

      try {
        const compressedBlob = await imageCompression(file, options)
        console.log(`Compressed ${file.name}: ${(file.size/1024/1024).toFixed(2)}MB → ${(compressedBlob.size/1024/1024).toFixed(2)}MB`)
        
        // Create new File object with correct .jpg extension
        const originalName = file.name.replace(/\.[^/.]+$/, '') // Remove original extension
        const newFileName = `${originalName}.jpg`
        const compressedFile = new File([compressedBlob], newFileName, {
          type: 'image/jpeg',
          lastModified: Date.now()
        })
        
        return compressedFile
      } catch (error) {
        console.error('Compression failed:', error)
        return file // Return original if compression fails
      }
    },

    // Scroll to section
    scrollToSection(section) {
      const element = this.$refs[section]
      if (element) {
        const offset = 100 // Account for sticky header
        const elementPosition = element.getBoundingClientRect().top
        const offsetPosition = elementPosition + window.pageYOffset - offset
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        })
      }
    },
    
    // Handle scroll for active section highlighting
    handleScroll() {
      const sections = ['organization', 'coordinator', 'fieldwork', 'banking', 'others']
      const scrollPosition = window.scrollY + 150
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const element = this.$refs[sections[i]]
        if (element && element.offsetTop <= scrollPosition) {
          this.activeSection = sections[i]
          break
        }
      }
    },
    
    // File upload handlers
    async onPaymentSlipChange(e) {
      const files = Array.from(e.target.files)
      const totalFiles = this.existingPaymentSlips.length + this.paymentSlips.length + files.length
      
      // Validate file count
      if (totalFiles > 5) {
        this.$swal.fire({
          icon: 'error',
          title: 'ข้อผิดพลาด',
          text: 'สามารถอัพโหลดได้ไม่เกิน 5 รูปเท่านั้น (รวมรูปเดิม)',
          confirmButtonText: 'ตรวจสอบ',
          confirmButtonColor: '#0d9488'
        })
        e.target.value = ''
        return
      }
      
      // Validate file size and type
      const validFiles = []
      const validPreviews = []
      
      this.isCompressing = true
      
      for (const file of files) {
        // Check file size (max 20MB for original file)
        if (file.size > 20 * 1024 * 1024) {
          this.$swal.fire({
            icon: 'error',
            title: 'ข้อผิดพลาด',
            text: `ไฟล์ ${file.name} มีขนาดเกิน 20 MB`,
            confirmButtonText: 'ตรวจสอบ',
            confirmButtonColor: '#0d9488'
          })
          continue
        }
        
        // Check file type
        if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
          this.$swal.fire({
            icon: 'error',
            title: 'ข้อผิดพลาด',
            text: `ไฟล์ ${file.name} ไม่ใช่ไฟล์รูปภาพที่รองรับ`,
            confirmButtonText: 'ตรวจสอบ',
            confirmButtonColor: '#0d9488'
          })
          continue
        }
        
        // Compress image
        this.currentCompressingFile = file.name
        this.compressionProgress = 0
        const compressedFile = await this.compressImage(file, 1.5)
        
        validFiles.push(compressedFile)
        validPreviews.push({
          url: URL.createObjectURL(compressedFile),
          name: file.name
        })
      }
      
      this.isCompressing = false
      this.currentCompressingFile = ''
      this.compressionProgress = 0
      
      this.paymentSlips = [...this.paymentSlips, ...validFiles]
      this.paymentSlipPreviews = [...this.paymentSlipPreviews, ...validPreviews]
      
      // Clear server error if exists
      if (this.serverError.payment_slips) {
        delete this.serverError.payment_slips
      }
    },
    
    async onRefuelingPhotoChange(e) {
      const files = Array.from(e.target.files)
      const totalFiles = this.existingRefuelingPhotos.length + this.refuelingPhotos.length + files.length
      
      // Validate file count
      if (totalFiles > 5) {
        this.$swal.fire({
          icon: 'error',
          title: 'ข้อผิดพลาด',
          text: 'สามารถอัพโหลดได้ไม่เกิน 5 รูปเท่านั้น (รวมรูปเดิม)',
          confirmButtonText: 'ตรวจสอบ',
          confirmButtonColor: '#0d9488'
        })
        e.target.value = ''
        return
      }
      
      // Validate file size and type
      const validFiles = []
      const validPreviews = []
      
      this.isCompressing = true
      
      for (const file of files) {
        // Check file size (max 20MB for original file)
        if (file.size > 20 * 1024 * 1024) {
          this.$swal.fire({
            icon: 'error',
            title: 'ข้อผิดพลาด',
            text: `ไฟล์ ${file.name} มีขนาดเกิน 20 MB`,
            confirmButtonText: 'ตรวจสอบ',
            confirmButtonColor: '#0d9488'
          })
          continue
        }
        
        // Check file type
        if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
          this.$swal.fire({
            icon: 'error',
            title: 'ข้อผิดพลาด',
            text: `ไฟล์ ${file.name} ไม่ใช่ไฟล์รูปภาพที่รองรับ`,
            confirmButtonText: 'ตรวจสอบ',
            confirmButtonColor: '#0d9488'
          })
          continue
        }
        
        // Compress image
        this.currentCompressingFile = file.name
        this.compressionProgress = 0
        const compressedFile = await this.compressImage(file, 1.5)
        
        validFiles.push(compressedFile)
        validPreviews.push({
          url: URL.createObjectURL(compressedFile),
          name: file.name
        })
      }
      
      this.isCompressing = false
      this.currentCompressingFile = ''
      this.compressionProgress = 0
      
      this.refuelingPhotos = [...this.refuelingPhotos, ...validFiles]
      this.refuelingPhotoPreviews = [...this.refuelingPhotoPreviews, ...validPreviews]
      
      // Clear server error if exists
      if (this.serverError.refueling_photos) {
        delete this.serverError.refueling_photos
      }
    },

    async onBookBankPhotoChange(e) {
      const files = Array.from(e.target.files)
      const totalFiles = this.existingBookBankPhotos.length + this.bookBankPhotos.length + files.length

      if (totalFiles > 1) {
        this.$swal.fire({
          icon: 'error',
          title: 'ข้อผิดพลาด',
          text: 'สามารถอัพโหลดได้ไม่เกิน 1 รูปเท่านั้น',
          confirmButtonText: 'ตรวจสอบ',
          confirmButtonColor: '#0d9488'
        })
        e.target.value = ''
        return
      }

      const validFiles = []
      const validPreviews = []

      this.isCompressing = true

      for (const file of files) {
        // Check file size (max 20MB for original file)
        if (file.size > 20 * 1024 * 1024) {
          this.$swal.fire({
            icon: 'error',
            title: 'ข้อผิดพลาด',
            text: `ไฟล์ ${file.name} มีขนาดเกิน 20 MB`,
            confirmButtonText: 'ตรวจสอบ',
            confirmButtonColor: '#0d9488'
          })
          continue
        }

        if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
          this.$swal.fire({
            icon: 'error',
            title: 'ข้อผิดพลาด',
            text: `ไฟล์ ${file.name} ไม่ใช่ไฟล์รูปภาพที่รองรับ`,
            confirmButtonText: 'ตรวจสอบ',
            confirmButtonColor: '#0d9488'
          })
          continue
        }

        // Compress image
        this.currentCompressingFile = file.name
        this.compressionProgress = 0
        const compressedFile = await this.compressImage(file, 1.5)

        validFiles.push(compressedFile)
        validPreviews.push({
          url: URL.createObjectURL(compressedFile),
          name: file.name
        })
      }

      this.isCompressing = false
      this.currentCompressingFile = ''
      this.compressionProgress = 0

      this.bookBankPhotos = [...this.bookBankPhotos, ...validFiles]
      this.bookBankPhotoPreviews = [...this.bookBankPhotoPreviews, ...validPreviews]

      if (this.serverError.book_bank_photos) {
        delete this.serverError.book_bank_photos
      }
    },
    
    // Remove files
    removePaymentSlip(index) {
      this.paymentSlips.splice(index, 1)
      URL.revokeObjectURL(this.paymentSlipPreviews[index].url)
      this.paymentSlipPreviews.splice(index, 1)
    },
    
    removeRefuelingPhoto(index) {
      this.refuelingPhotos.splice(index, 1)
      URL.revokeObjectURL(this.refuelingPhotoPreviews[index].url)
      this.refuelingPhotoPreviews.splice(index, 1)
    },

    removeBookBankPhoto(index) {
      this.bookBankPhotos.splice(index, 1)
      URL.revokeObjectURL(this.bookBankPhotoPreviews[index].url)
      this.bookBankPhotoPreviews.splice(index, 1)
    },
    
    // Form submission
    async submitForm() {
      // Client-side validation
      const totalPaymentSlips = this.existingPaymentSlips.length + this.paymentSlips.length
      const totalBookBank = this.existingBookBankPhotos.length + this.bookBankPhotos.length

      if (totalPaymentSlips === 0) {
        this.$swal.fire({
          icon: 'warning',
          title: 'กรุณาเพิ่มรูปสลิป',
          text: 'กรุณาอัพโหลดรูปสลิปการจ่ายเงินอย่างน้อย 1 รูป',
          confirmButtonText: 'ตรวจสอบ',
          confirmButtonColor: '#0d9488'
        })
        this.scrollToSection('banking')
        return
      }

      // Ensure book bank photo is provided (required single file)
      if (totalBookBank === 0) {
        this.$swal.fire({
          icon: 'warning',
          title: 'กรุณาเพิ่มรูปถ่ายหน้าบัญชี',
          text: 'กรุณาอัพโหลดรูปถ่ายหน้าบัญชีอย่างน้อย 1 รูป',
          confirmButtonText: 'ตรวจสอบ',
          confirmButtonColor: '#0d9488'
        })
        this.scrollToSection('banking')
        return
      }
      
      // Start loading
      this.isSubmitting = true
      this.$Progress.start()
      
      // Prepare FormData
      const formDataToSend = new FormData()
      
      // Append text fields
      Object.keys(this.formData).forEach(key => {
        if (key === 'field_provinces') {
          // Convert array to JSON string
          formDataToSend.append(key, JSON.stringify(this.formData[key]))
        } else if (this.formData[key] !== null) {
          formDataToSend.append(key, this.formData[key])
        }
      })
      
      // Append files to remove
      this.filesToRemove.forEach(id => {
        formDataToSend.append('remove_file_ids[]', id)
      })

      // Append payment slips as array entries
      this.paymentSlips.forEach(file => {
        formDataToSend.append('payment_slips[]', file)
      })
      
      // Append book bank photos (optional)
      this.bookBankPhotos.forEach(file => {
        formDataToSend.append('book_bank_photos[]', file)
      })

      // Append refueling photos (optional)
      this.refuelingPhotos.forEach(file => {
        formDataToSend.append('refueling_photos[]', file)
      })
      
      try {
        const response = await FloodReliefMissionService.updateRequest(this.encryptedId, formDataToSend)
        
        this.$Progress.finish()
        this.isSubmitting = false
        this.serverError = {}
        
        // Show success message
        this.$swal.fire({
          icon: 'success',
          title: 'บันทึกการแก้ไขสำเร็จ',
          html: response.data.message,
          confirmButtonText: 'รับทราบ',
          confirmButtonColor: '#0d9488'
        }).then(() => {
          // Redirect to status page
          this.$router.push({ name: 'FloodReliefMissionStatus' })
        })
        
      } catch (error) {
        this.$Progress.fail()
        this.isSubmitting = false
        
        if (error.response) {
          // Handle validation errors
          if (error.response.status === 422) {
            this.serverError = error.response.data.errors || {}
            
            this.$swal.fire({
              icon: 'error',
              title: 'กรุณาตรวจสอบข้อมูล',
              text: 'กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง',
              confirmButtonText: 'ตรวจสอบ',
              confirmButtonColor: '#0d9488'
            })
            
            // Scroll to first error
            this.scrollToFirstError()
          } 
          // Handle authentication error
          else if (error.response.status === 401) {
            this.$store.dispatch('authMember/unauthenticateResetting')
            this.$swal.fire({
              icon: 'warning',
              title: 'กรุณาเข้าสู่ระบบ',
              text: 'กรุณาเข้าสู่ระบบเพื่อส่งคำขอ',
              confirmButtonText: 'เข้าสู่ระบบ',
              confirmButtonColor: '#0d9488'
            }).then(() => {
              this.$router.push({ name: 'Login' })
            })
          }
          // Handle other errors
          else {
            this.$swal.fire({
              icon: 'error',
              title: 'เกิดข้อผิดพลาด',
              text: error.response.data.message || 'ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง',
              confirmButtonText: 'ตรวจสอบ',
              confirmButtonColor: '#0d9488'
            })
          }
        } else {
          this.$swal.fire({
            icon: 'error',
            title: 'เกิดข้อผิดพลาด',
            text: 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต',
            confirmButtonText: 'ตรวจสอบ',
            confirmButtonColor: '#0d9488'
          })
        }
      }
    },
    
    // Scroll to first error field
    scrollToFirstError() {
      const sections = ['organization', 'coordinator', 'fieldwork', 'banking', 'others']
      
      for (const section of sections) {
        const element = this.$refs[section]
        if (element) {
          const errorFields = element.querySelectorAll('.border-red-500')
          if (errorFields.length > 0) {
            this.scrollToSection(section)
            break
          }
        }
      }
    }
  }
}
</script>

<style scoped>
/* Custom styles for smooth scrolling */
html {
  scroll-behavior: smooth;
}

/* Enhance input focus states */
input:focus,
select:focus,
textarea:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
}

/* Checkbox styling */
input[type="checkbox"]:checked {
  background-color: #2563eb;
  border-color: #2563eb;
}

/* Custom scrollbar for better UX */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
}

::-webkit-scrollbar-thumb {
  background: #0d9488;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #0f766e;
}

/* Form Specific Styles */
.form-header {
  background: linear-gradient(to right, #0d9488, #0891b2);
  color: white;
  padding: 3rem 0;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.form-section {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  scroll-margin-top: 6rem;
}

.section-teal {
  border-top: 4px solid #0d9488;
}

.section-cyan {
  border-top: 4px solid #0891b2;
}

.section-blue {
  border-top: 4px solid #2563eb;
}

.section-indigo {
  border-top: 4px solid #4f46e5;
}

.section-number {
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 1.125rem;
}

.section-number-teal {
  background-color: #0d9488;
}

.section-number-cyan {
  background-color: #0891b2;
}

.section-number-blue {
  background-color: #2563eb;
}

.section-number-indigo {
  background-color: #4f46e5;
}

.section-purple {
  border-top: 4px solid #7c3aed;
}

.section-number-purple {
  background-color: #7c3aed;
}

/* Compression Overlay Styles */
.compression-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.compression-modal {
  background-color: white;
  border-radius: 1rem;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  max-width: 320px;
  width: 90%;
}

.compression-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top-color: #0d9488;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.compression-progress-bar {
  width: 100%;
  height: 8px;
  background-color: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.compression-progress-fill {
  height: 100%;
  background: linear-gradient(to right, #0d9488, #0891b2);
  border-radius: 4px;
  transition: width 0.3s ease;
}
</style>